import React, { useEffect } from "react"
import {
	Autocomplete, Box, FormControl, FormControlLabel, FormHelperText, InputLabel, MenuItem, Radio, RadioGroup, Select, TextField, Typography,
} from "@mui/material"
import { dsColorNew } from "@palette"
import { dsRepricingDrawerData, dsRepricingDrawerErrors } from "Pages/DsRepricing/Page/@signals"
import { PREPAID_OPTIONS, prepaidKey, prepaidValue, useAxisCatalog } from "Pages/DsRepricing/Setup/@services/axes"
import { inputSx } from "Pages/DsRepricing/Setup/@components/formStyles"
import { DEFAULT_COMPARE } from "Pages/DsRepricing/Page/@services/adapters"

// Card 6 of Scope, "Which kinds of offer", under "What it is compared
// against" (CU-869fhuhy6, brief v3 criterion 12, UX v3 D18) — who the
// rule is compared against, in one radio:
//   same kind as my product (default) · every kind · a specific kind
// Replaces the two switches of CU-869fh646w; `compareAgainst = { mode,
// tariffType, insuranceId }` of the drawer payload travels with the save.
// "A specific kind" with neither axis publishes `compareAgainst: false`
// into the drawer errors: the step blocks like on a missing name
// (criterion 12: tariff type and/or insurance chosen).
const OPTIONS = [
	{ mode: 'same', label: 'The same kind as my product',
	  hint: 'Like with like: a competitor only counts if it sells the same tariff type and insurance as the product\'s own row on that site.' },
	{ mode: 'every', label: 'Every kind',
	  hint: 'Prepaid and pay-on-arrival, any insurance — for a page that mixes them, where you want to be first regardless of kind.' },
	{ mode: 'specific', label: 'A specific kind',
	  hint: 'Measure the product against one kind of offer, whatever the product\'s own kind is — e.g. a premium car against competitors sold with excess.' },
]

export default function DsRepricingCompareAgainst() {
	const data = dsRepricingDrawerData.value || {}
	const axes = useAxisCatalog()
	const c = { ...DEFAULT_COMPARE, ...(data.compareAgainst || {}) }
	const complete = c.mode !== 'specific' || c.tariffType != null || c.insuranceId != null

	useEffect(() => {
		dsRepricingDrawerErrors.value = { ...dsRepricingDrawerErrors.value, compareAgainst: complete }
	}, [complete, data.id])

	const change = (patch) => {
		const next = { ...c, ...patch }
		if (next.mode !== 'specific') { next.tariffType = null; next.insuranceId = null }
		dsRepricingDrawerData.value = { ...dsRepricingDrawerData.value, compareAgainst: next }
	}

	return (
		<Box>
			<RadioGroup value={c.mode} onChange={e => change({ mode: e.target.value })}>
				{OPTIONS.map(o => {
					const on = c.mode === o.mode
					return (
						// The chosen option reads as the wizard's selected pill: soft blue with an app-blue edge
						<Box key={o.mode} sx={{ p: 1.25, mb: 1, borderRadius: '10px',
						                        border: `1.5px solid ${on ? dsColorNew.appBlue : dsColorNew.grayBorder}`,
						                        bgcolor: on ? dsColorNew.pillActiveBg : dsColorNew.white,
						                        transition: 'border-color .15s, background-color .15s' }}>
							<FormControlLabel value={o.mode} control={<Radio size="small"/>}
							                  label={<Typography sx={{ fontSize: 13, fontWeight: 600, color: on ? dsColorNew.appBlue : dsColorNew.textDark }}>{o.label}</Typography>}/>
							<Typography component="span" sx={{ display: 'block', pl: 4, fontSize: 12, color: dsColorNew.textMuted, lineHeight: 1.45 }}>{o.hint}</Typography>
							{o.mode === 'specific' && on && (
								<Box sx={{ display: 'flex', gap: 1.5, pl: 4, mt: 1.25, flexWrap: 'wrap' }}>
									<FormControl size="small" sx={{ ...inputSx, width: 180 }}>
										<InputLabel shrink>Tariff type</InputLabel>
										<Select label="Tariff type" notched displayEmpty value={prepaidKey(c.tariffType)}
										        onChange={e => change({ tariffType: prepaidValue(e.target.value) })}>
											<MenuItem value=""><em>Any tariff type</em></MenuItem>
											{PREPAID_OPTIONS.map(p => <MenuItem key={p.code} value={p.code}>{p.name}</MenuItem>)}
										</Select>
									</FormControl>
									<Autocomplete size="small" options={axes.insurances} sx={{ width: 260 }}
									              value={axes.insurances.find(i => i.code === String(c.insuranceId)) || null}
									              getOptionLabel={(i) => i.name} isOptionEqualToValue={(a, b) => a.code === b.code}
									              onChange={(_, i) => change({ insuranceId: i ? Number(i.code) : null })}
									              renderInput={(params) => <TextField {...params} label="Insurance" InputLabelProps={{ shrink: true }}
									                                                 placeholder={axes.loading ? 'Loading…' : axes.insurances.length ? 'Any insurance' : 'No insurance catalogue'} sx={inputSx}/>}/>
									{!complete && (
										<FormHelperText error sx={{ width: '100%', m: 0 }}>
											Pick a tariff type, an insurance, or both — a specific kind needs at least one. For neither, choose "Every kind".
										</FormHelperText>
									)}
								</Box>
							)}
						</Box>
					)
				})}
			</RadioGroup>
		</Box>
	)
}
